import {
	BigNum,
	BN,
	PublicKey,
	ReferrerNameAccount,
	SPOT_MARKET_BALANCE_PRECISION_EXP,
	SpotBalanceType,
	SpotMarketConfig,
	User,
} from '@velocity-exchange/sdk';
import {
	UISerializableDepositRecord,
	UISerializableFundingPaymentRecord,
	UISerializableLiquidationRecord,
	UISerializableOrderActionRecord,
	UISerializableSettlePnlRecord,
	UISerializableSwapRecord,
	UISerializableInsuranceFundStakeRecord,
	ENUM_UTILS,
	getUserKey,
} from '@velocity-exchange/common';
import { create } from 'zustand';
import type { ParentOrderInfo } from 'src/@types/historyTables';
import produce from 'immer';

export type AccountSpotBalance = {
	asset: SpotMarketConfig;
	balance: BigNum;
	balanceType: SpotBalanceType;
	quoteValue: BigNum;
	accountId: number;
	accountName: string;
	accountKey: string;
};

export const ACCOUNT_INITIAL_STATE = (userKey?: string): AccountData => {
	return {
		userKey: userKey ?? '',
		userId: 0,
		name: '',
		poolId: 0,
		pubKey: PublicKey.default,
		authority: PublicKey.default,
		client: undefined,
		tradeHistory: {
			records: [],
			totalCount: 0,
			loading: false,
			initialHistoryLoaded: false,
			maxRecordLimit: 1000,
		},
		orderHistory: {
			records: [],
			totalCount: 0,
			loading: false,
			initialHistoryLoaded: false,
			marketOrderCounts: {
				perp: 0,
				spot: 0,
			},
			maxRecordLimit: 1000,
		},
		fundingHistory: {
			records: [],
			totalCount: 0,
			loading: false,
			initialHistoryLoaded: false,
			maxRecordLimit: 1000,
		},
		liquidationHistory: {
			records: [],
			totalCount: 0,
			loading: false,
			initialHistoryLoaded: false,
			maxRecordLimit: 1000,
		},
		depositWithdrawalHistory: {
			records: [],
			totalCount: 0,
			loading: false,
			initialHistoryLoaded: false,
			maxRecordLimit: 1000,
		},
		rewardHistory: {
			records: [],
			totalCount: 0,
			loading: false,
			initialHistoryLoaded: false,
			maxRecordLimit: 1000,
		},
		swapHistory: {
			records: [],
			totalCount: 0,
			loading: false,
			initialHistoryLoaded: false,
			maxRecordLimit: 1000,
		},
		settlePnlHistory: {
			records: [],
			totalCount: 0,
			loading: false,
			initialHistoryLoaded: false,
			maxRecordLimit: 1000,
		},
		ifStakeHistory: {
			records: [],
			totalCount: 0,
			loading: false,
			initialHistoryLoaded: false,
			maxRecordLimit: 1000,
		},
		pnlInfo: {},
		balanceCount: 0,
		marginEnabled: false,
		delegate: PublicKey.default,
		spotBalances: [],
		rent: {
			amount: BigNum.zero(SPOT_MARKET_BALANCE_PRECISION_EXP),
			loaded: false,
		},
	};
};

const EMPTY_BALANCES: AccountSpotBalance[] = [];

export type HistoryBaseState<T> = {
	records: T[];
	totalCount: number;
	loading: boolean;
	initialHistoryLoaded: boolean;
};

export interface HistoryBaseStateWithMaxRecordLimit<
	T,
> extends HistoryBaseState<T> {
	maxRecordLimit: number;
}

interface OrderHistoryWithMarketCounts extends HistoryBaseStateWithMaxRecordLimit<ParentOrderInfo> {
	marketOrderCounts: {
		spot: number;
		perp: number;
	};
}

export interface AccountDataRent {
	amount: BigNum;
	loaded: boolean;
}

export interface AccountData {
	userKey: string;
	userId: number;
	name: string;
	pubKey: PublicKey;
	authority: PublicKey;
	/**
	 * Subscription state lives on the client itself (`client.isSubscribed`), which
	 * teardown flips. Don't mirror it onto `AccountData` — a copy taken at load time
	 * cannot be kept honest, and reads on an unsubscribed client throw.
	 */
	client: User | undefined;
	tradeHistory: HistoryBaseStateWithMaxRecordLimit<UISerializableOrderActionRecord>;
	orderHistory: OrderHistoryWithMarketCounts;
	fundingHistory: HistoryBaseStateWithMaxRecordLimit<UISerializableFundingPaymentRecord>;
	liquidationHistory: HistoryBaseStateWithMaxRecordLimit<UISerializableLiquidationRecord>;
	depositWithdrawalHistory: HistoryBaseStateWithMaxRecordLimit<UISerializableDepositRecord>;
	rewardHistory: HistoryBaseStateWithMaxRecordLimit<UISerializableDepositRecord>;
	swapHistory: HistoryBaseStateWithMaxRecordLimit<UISerializableSwapRecord>;
	settlePnlHistory: HistoryBaseStateWithMaxRecordLimit<UISerializableSettlePnlRecord>;
	ifStakeHistory: HistoryBaseStateWithMaxRecordLimit<UISerializableInsuranceFundStakeRecord>;
	pnlInfo: Record<
		number,
		{
			leverageBeforeClose: number | null;
			collateralBeforeClose: BN | null;
		}
	>;
	balanceCount: number;
	marginEnabled: boolean;
	delegate: PublicKey;
	isDelegatedTo?: boolean;
	isDelegatedAway?: boolean;
	spotBalances: AccountSpotBalance[];
	rent: AccountDataRent;
	poolId: number;
}

interface ReferrerNameAccountWithDecoded extends ReferrerNameAccount {
	decodedName: string;
}

export interface VelocityAccountsStore {
	set: (x: (s: VelocityAccountsStore) => void) => void;
	get: () => VelocityAccountsStore;
	/**
	 * If true, the accounts have been loaded initially
	 */
	hasLoadedAccounts: boolean;
	currentUserKey: string | undefined;
	currentUserExists: boolean;
	/**
	 * returns undefined if the account is not loaded
	 */
	accounts: { [userKey: string]: AccountData | undefined };
	/**
	 * Referrer names created by the current authority
	 * Because only subaccount 0 can create these, not putting it under each account.
	 */
	referrerNameAccounts: {
		accounts: ReferrerNameAccountWithDecoded[];
		// No total count because it's probably not important?
		// But we can add it later if needed
		loading: boolean;
		initialReferrerNameAccountsLoaded: boolean;
	};
	userAccountNotInitialized: boolean;
	getAggregateAccountsSpotBalances: () => AccountSpotBalance[];
	/** Can be undefined if wallet not connected */
	getCurrentUserAccount: () => AccountData | undefined;
	/**
	 * Meant to be used with velocityClient.switchActiveUser(). Use useUpdateCurrentSubaccount hook instead.
	 */
	_updateCurrentSubAccount: (
		newSubaccountId: number,
		authority: PublicKey
	) => void;
	resetAccountsState: () => void;
	deletedAccounts?: Partial<AccountData>[];
}

const INITIAL_ACCOUNTS_STATE: Omit<VelocityAccountsStore, 'set' | 'get'> = {
	currentUserKey: '',
	currentUserExists: false,
	accounts: {},
	referrerNameAccounts: {
		accounts: [],
		loading: false,
		initialReferrerNameAccountsLoaded: false,
	},
	userAccountNotInitialized: false,
	deletedAccounts: [],
	hasLoadedAccounts: false,
	getAggregateAccountsSpotBalances: () => EMPTY_BALANCES,
	getCurrentUserAccount: () => undefined,
	_updateCurrentSubAccount: () => {},
	resetAccountsState: () => {},
};

const useVelocityAccountStore = create<VelocityAccountsStore>((set, get) => {
	const setState = (fn: (s: VelocityAccountsStore) => void) => {
		set(produce(fn) as (s: VelocityAccountsStore) => VelocityAccountsStore);
	};

	return {
		set: setState,
		get: () => get(),
		...INITIAL_ACCOUNTS_STATE,
		getAggregateAccountsSpotBalances: () => {
			const allAccountsBalances = Object.values(get().accounts)
				.map((account) => account?.spotBalances as AccountSpotBalance[])
				.filter(Boolean)
				.flat() as AccountSpotBalance[];

			return allAccountsBalances.length > 0
				? allAccountsBalances
				: EMPTY_BALANCES;
		},
		getCurrentUserAccount: () => {
			return get().accounts[get().currentUserKey ?? ''] as AccountData;
		},
		_updateCurrentSubAccount: (
			newSubaccountId: number,
			authority: PublicKey
		) => {
			const userKey = getUserKey(newSubaccountId, authority);
			setState((s) => {
				s.currentUserKey = userKey;
			});
		},
		resetAccountsState: () => {
			setState((s) => {
				s.currentUserKey = '';
				s.accounts = {};
				s.deletedAccounts = [];
				s.userAccountNotInitialized = false;
				s.hasLoadedAccounts = false;
			});
		},
	};
});

const checkIfTwoAccountSpotBalanceAreEqual = (
	balance1: AccountSpotBalance,
	balance2: AccountSpotBalance
): boolean => {
	return (
		balance1.asset.marketIndex === balance2.asset.marketIndex &&
		balance1.balance.eq(balance2.balance) &&
		ENUM_UTILS.match(balance1.balanceType, balance2.balanceType) &&
		balance1.quoteValue.eq(balance2.quoteValue) &&
		balance1.accountId === balance2.accountId &&
		balance1.accountName === balance2.accountName &&
		balance1.accountKey === balance2.accountKey
	);
};

export const checkIfAccountsSpotBalancesAreEqual = (
	balances1: AccountSpotBalance[],
	balances2: AccountSpotBalance[]
): boolean => {
	if (balances1.length !== balances2.length) {
		return false;
	}

	return balances1.every((balance1, index) => {
		return checkIfTwoAccountSpotBalanceAreEqual(balance1, balances2[index]);
	});
};

export default useVelocityAccountStore;
